import { Icon, Typography, Divider } from '@scale/scaleui';
import { useMemo, useState } from 'react';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useScrollToStep } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useScrollToStep';
import { isStepVisible, isConditionallyVisible } from '@scale/shared/chat/api';
import { ReviewLevel } from '@scale/shared-types';
import { Claim } from '@scale/shared/types/queue';
import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { timeRemaining } from 'react-scale/components/scalers/tasks/OutlierTaskHeader/ChipTaskExpiration';
import { TaskOutlineSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/TaskOutlineSection';
import { twMerge } from 'tailwind-merge';

type TaskOutlineProps = {
  reviewLevel?: ReviewLevel;
  claim?: Claim;
  inAssessment?: boolean;
};

export const TaskOutline = ({ reviewLevel, claim, inAssessment }: TaskOutlineProps) => {
  const responses = useChatStoreExtract(state => state.responses);
  const stepParams = useChatStoreExtract(state => state.stepParams);
  const completedSteps = useChatStoreExtract(state => state.completedSteps);
  const scrollToStep = useScrollToStep();

  const { claimedUntil, now } = useClaimTime(claim);
  const { days, hours, minutes } = timeRemaining(claimedUntil, now);

  const maxTimeString = () => {
    return `Max. ${days}${hours}${minutes}`;
  };

  const visibleStepParams = useMemo(() => {
    return stepParams.filter(
      (step, idx) =>
        !('skip_selection' in step.params) &&
        isStepVisible(step, reviewLevel) &&
        step.title &&
        isConditionallyVisible(step, responses, idx),
    );
  }, [stepParams, reviewLevel, responses]);

  if (visibleStepParams.length <= 1) {
    return <></>;
  }

  type AccordionProps = {
    title: React.ReactNode;
    content: React.ReactNode;
    startsOpened?: boolean;
  };
  const Accordion = ({ title, content, startsOpened = false }: AccordionProps) => {
    const [isOpen, setIsOpen] = useState(startsOpened);

    return (
      <div
        className={twMerge(
          'border-2 rounded w-[436px]',
          isOpen ? 'border-misc-periwinkle100' : 'border-neutral-200',
        )}
      >
        {' '}
        <button
          onClick={() => setIsOpen(prev => !prev)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            <span>{isOpen ? <Icon icon="chevron-up" /> : <Icon icon="chevron-down" />}</span>
            {title}
          </div>
        </button>
        {isOpen && content}
      </div>
    );
  };
  return (
    <>
      <div className={`text-neutral-700 ${inAssessment ? 'text-md mt-4' : 'text-lg'}`}>
        {'Task Outline'}
      </div>
      <Accordion
        title={
          <div className="flex items-center space-x-2">
            <Typography.Subtitle4>Current Task</Typography.Subtitle4>
            <Typography.Paragraph3
              color={'text-neutral-500'}
            >{`• ${maxTimeString()}`}</Typography.Paragraph3>
          </div>
        }
        content={
          <>
            <div className="flex">
              <Divider className="mx-2" />
            </div>
            <div className="p-2">
              {visibleStepParams.map(step => {
                return TaskOutlineSection({ step, completedSteps, scrollToStep });
              })}
            </div>
          </>
        }
        startsOpened={true}
      />
    </>
  );
};
